import { useState } from "react";
import { api } from "@/lib/api";
import { CATEGORIES } from "@/lib/categories";
import { EditableText } from "@/components/Editable";
import { Bell, Check } from "lucide-react";
import { toast } from "sonner";

export default function NotifySection() {
  const [email, setEmail] = useState("");
  const [picked, setPicked] = useState([]);
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);

  const toggle = (slug) => {
    setPicked((p) =>
      p.includes(slug) ? p.filter((x) => x !== slug) : [...p, slug],
    );
  };

  const submit = async (e) => {
    e.preventDefault();
    if (!email) {
      toast.error("Please share your email");
      return;
    }
    if (picked.length === 0) {
      toast.error("Choose at least one type to follow");
      return;
    }
    setSubmitting(true);
    try {
      await api.post("/subscribers", { email, categories: picked });
      setDone(true);
      toast.success("You're on the list");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Couldn't sign you up");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <section
      data-testid="notify-section"
      className="bg-[var(--ark-bone)] border-y border-[var(--ark-border)]"
    >
      <div className="max-w-7xl mx-auto px-6 md:px-10 py-20 grid grid-cols-1 lg:grid-cols-12 gap-10">
        <div className="lg:col-span-5 reveal">
          <div className="inline-flex items-center gap-2 text-[0.72rem] uppercase tracking-[0.28em] text-[var(--ark-accent)]">
            <Bell size={12} /> Get the first look
          </div>
          <EditableText
            contentKey="notify_heading"
            fallback="Be first to know."
            as="h2"
            className="font-display text-4xl md:text-5xl tracking-tight mt-3 leading-[1.05]"
          />
          <EditableText
            contentKey="notify_body"
            fallback="The best pieces leave the workshop fast. Pick the types you love and we'll send a note the moment something new arrives — never more than once per piece."
            as="p"
            multiline
            className="mt-5 text-[var(--ark-muted)] leading-relaxed max-w-md"
          />
        </div>

        <div className="lg:col-span-7 reveal" style={{ animationDelay: "100ms" }}>
          {done ? (
            <div
              data-testid="notify-success"
              className="border border-[var(--ark-border)] bg-white p-10 text-center"
            >
              <div className="h-12 w-12 rounded-full bg-[var(--ark-accent)] text-white mx-auto flex items-center justify-center">
                <Check size={20} />
              </div>
              <h3 className="font-display text-2xl mt-4">You're on the list.</h3>
              <p className="text-sm text-[var(--ark-muted)] mt-2 max-w-sm mx-auto">
                We'll email{" "}
                <span className="text-black font-medium">{email}</span> the
                next time we add a{" "}
                {picked
                  .map((s) =>
                    CATEGORIES.find((c) => c.slug === s)?.name.toLowerCase(),
                  )
                  .filter(Boolean)
                  .join(", ")}
                .
              </p>
              <button
                type="button"
                onClick={() => {
                  setDone(false);
                  setPicked([]);
                  setEmail("");
                }}
                className="ark-btn ark-btn-ghost mt-6"
              >
                Sign up another email
              </button>
            </div>
          ) : (
            <form
              data-testid="notify-form"
              onSubmit={submit}
              className="border border-[var(--ark-border)] bg-white p-6 md:p-8"
            >
              <div className="text-[0.65rem] uppercase tracking-[0.18em] text-[var(--ark-muted)] mb-3">
                Pick what you'd like to hear about
              </div>
              <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-2 mb-6">
                {CATEGORIES.map((c) => {
                  const on = picked.includes(c.slug);
                  return (
                    <button
                      key={c.slug}
                      type="button"
                      data-testid={`notify-cat-${c.slug}`}
                      onClick={() => toggle(c.slug)}
                      className={`text-left text-sm border px-3 py-2.5 transition-all ${
                        on
                          ? "border-[var(--ark-accent)] bg-[var(--ark-bone)] text-black"
                          : "border-[var(--ark-border)] text-[var(--ark-muted)] hover:border-black hover:text-black"
                      }`}
                    >
                      <span className="inline-flex items-center gap-2">
                        <span
                          className={`h-3 w-3 border ${on ? "bg-[var(--ark-accent)] border-[var(--ark-accent)]" : "border-[var(--ark-border)]"} flex items-center justify-center`}
                        >
                          {on && <Check size={9} className="text-white" />}
                        </span>
                        {c.name}
                      </span>
                    </button>
                  );
                })}
              </div>

              <div className="flex flex-col sm:flex-row items-stretch gap-3">
                <input
                  type="email"
                  required
                  data-testid="notify-email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="you@example.com"
                  className="ark-input !border flex-1 !px-4 !py-3"
                />
                <button
                  type="submit"
                  disabled={submitting}
                  data-testid="notify-submit"
                  className="ark-btn whitespace-nowrap disabled:opacity-60"
                >
                  {submitting ? "Saving…" : "Notify me"}
                </button>
              </div>
              <p className="text-[0.65rem] uppercase tracking-[0.16em] text-[var(--ark-muted)] mt-4">
                One quick email per new piece. Unsubscribe any time.
              </p>
            </form>
          )}
        </div>
      </div>
    </section>
  );
}
